import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Overflow16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { Toggle } from 'ui/toggle';
import { body2, colors, caption, fontWeights, spacing } from 'ui/theme';
import { ActionMenu } from 'ui/actionMenu';
import { MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';
import { AlertsWebhookListItem } from '../../types';
import { useToggleWebhook } from '../../hooks/useToggleWebhook';
import { useAlertDestinationMenuOptions } from './useAlertDestinationMenuOptions';

const WebhookListItem = styled.div`
  ${body2}
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${spacing.base};
`;

const WebhookListItemInfo = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: left;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const WebhookName = styled.span<{ $maxWidth?: string }>`
  ${body2}
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;
  max-width: 100%;
  font-weight: ${fontWeights.medium};
`;

const WebhookEndpoint = styled.span`
  ${caption}
  color: ${colors.secondaryText};
`;

const WebhookListItemInteractable = styled.div`
  text-align: left;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  margin-left: ${spacing.base};
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type AlertWebhookItemProps = {
  webhook: AlertsWebhookListItem;
};

export const AlertWebhook = ({ webhook }: AlertWebhookItemProps) => {
  const { enabled, endpoint, id, name } = webhook;
  const menuOptions = useAlertDestinationMenuOptions(id);

  const { isOverflowing, elementRef } = useIsOverflowing<HTMLSpanElement>();

  const { toggleWebhook } = useToggleWebhook();

  const toggle = () => {
    const variables = { id, enabled: !enabled };
    toggleWebhook({ variables });
  };

  return (
    <WebhookListItem data-test={`webhook-list-item-${id}`} key={id}>
      <WebhookListItemInfo>
        <StyledTooltip tooltipContent={name} isEnabled={isOverflowing}>
          <WebhookName ref={elementRef}>{name}</WebhookName>
        </StyledTooltip>
        <WebhookEndpoint>{endpoint}</WebhookEndpoint>
      </WebhookListItemInfo>
      <WebhookListItemInteractable>
        <ActionMenu
          options={menuOptions}
          align={MENU_ALIGNMENTS.RIGHT}
          data-test={`webhook-list-item-action-menu-${id}`}
        >
          <IconButton size={BUTTON_SIZES.SMALL} variant={BUTTON_VARIANTS.GHOST}>
            <Overflow16 />
          </IconButton>
        </ActionMenu>
        <Toggle id={`${id}`} isActive={enabled} onClick={toggle} />
      </WebhookListItemInteractable>
    </WebhookListItem>
  );
};
